// Copyright (c) Bentley Systems
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  checkInternalServerError,
  getParametersFromUrlString,
  getTranslatedStringFromParameters,
  getUrlBasedOnEnv,
  setErrorState,
} from "../../Helpers/helper-functions";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAccessToken } from "../../app/authorization-slice";
import {
  Alert,
  Button,
  LabeledSelect,
  ProgressRadial,
  Text,
} from "@itwin/itwinui-react";
import { mapDataToModels } from "../../Helpers/map-response";
import { SystemType } from "./Models/system-type";
import SystemProperties from "./SystemProperties";
import { SystemProperty } from "./Models/system-property";
import { SaveUserSystemCredentialDto } from "./Models/save-user-system-credential-dto";
import { IntegPackage } from "./Models/integ-package";
import { DynamicObject } from "../../Helpers/dynamic-object";
import { SystemInList } from "../CredentialsList/Models/system-in-list";
import { setConfirmation } from "../../app/confirmation-slice";
import LabeledInputComponent from "../../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import { EfFieldValidationTypeEnum } from "../../Helpers/enumerators";
import { ConfigManager } from "../../Helpers/config-manager";
import { useToastNotificationService } from "../../Helpers/ToastNotificationService";
import {
  getMissingRequiredProperties,
  getVisibleProperties,
} from "./token-auth-rules";

export type CredentialsFormProps = {
  params?: string;
  iTwinId: string;
  system?: SystemInList;
  ///is open directly from URL or is it open in InformationPanel in elist
  isFromUrl: boolean;
  isStandaloneMode?: boolean;
  closeFrollFunction?: (refreshList: boolean) => void;
  navigateToInformationPanelFunction?: (navFrolUrl: string) => void;
  setInforPanelHeaderFunction?: (header: string) => void;
};

const CredentialsForm = (props: CredentialsFormProps) => {
  const { showToast } = useToastNotificationService();
  const dispatch = useAppDispatch();
  const { params } = useParams();
  const urlParameters = useMemo(
    () => getParametersFromUrlString(params),
    [params],
  );
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isPropertiesLoading, setIsPropertiesLoading] =
    useState<boolean>(false);
  const [isEditingExisting, setIsEditingExisting] = useState<boolean>(false);

  const parameters =
    props && props.isFromUrl
      ? urlParameters
      : getParametersFromUrlString(props.params);

  const accessToken = useAppSelector(selectAccessToken);
  const [connName, setConnName] = useState<string>();
  const [systemTypes, setSystemTypes] = useState<SystemType[]>([]);
  const [systemType, setSystemType] = useState<SystemType>();
  const [integPackages, setIntegPackages] = useState<IntegPackage[]>([]);
  const [integPackage, setIntegPackage] = useState<IntegPackage>();
  const [systemId, setSystemId] = useState<string>();
  const [systemProperties, setSystemProperties] = useState<SystemProperty[]>(
    [],
  );
  const [notEditedSysPropsDynObj, setNotEditedSysPropsDynObj] =
    useState<DynamicObject>();
  const [connNameStatus, setConnNameStatus] = useState<
    "positive" | "warning" | undefined
  >(undefined);
  const [systemIdStatus, setSystemIdStatus] = useState<
    "positive" | "warning" | undefined
  >(undefined);

  const abortControllerRef = useRef<AbortController | null>(null);

  const setInfoPanelHeader = useCallback(
    (vmHeader: string | undefined) => {
      if (
        props.setInforPanelHeaderFunction !== undefined &&
        vmHeader !== undefined
      ) {
        const header = getTranslatedStringFromParameters(vmHeader, parameters);
        props.setInforPanelHeaderFunction(header as string);
      }
    },
    [parameters, props],
  );

  useEffect(() => {
    const headerStr = `${props.system === undefined ? "New" : "Edit"} cloud credentials`;
    setInfoPanelHeader(headerStr);
  }, [props.system, setInfoPanelHeader]);

  const hasMissingRequiredProps = useMemo(
    () => getMissingRequiredProperties(systemProperties).length > 0,
    [systemProperties],
  );

  // Standalone mode validates the typed System ID; traditional mode picks it from the package's list.
  const isFormValid = !!(
    systemType &&
    connName &&
    systemId &&
    connNameStatus !== "warning" &&
    (!props.isStandaloneMode || systemIdStatus !== "warning") &&
    !hasMissingRequiredProps
  );

  const areSystemPropsWithoutChange = useCallback((): boolean => {
    if (notEditedSysPropsDynObj !== undefined) {
      const propsDynObj = getSystemPropsDynObject(systemProperties);
      const keys = Object.keys(notEditedSysPropsDynObj);
      return keys.every((k) => notEditedSysPropsDynObj[k] === propsDynObj[k]);
    }
    return true;
  }, [notEditedSysPropsDynObj, systemProperties]);

  const wasFormChanged = useCallback(() => {
    if (props.system !== undefined) {
      // Editing: check if any field changed
      return !(
        props.system.systemName === connName &&
        props.system.systemType === systemType?.name &&
        props.system.systemId === systemId &&
        areSystemPropsWithoutChange()
      );
    } else {
      // New credential
      if (props.isStandaloneMode) {
        // Don't check integPackage in standalone mode
        return !!(connName || systemType || systemId || !areSystemPropsWithoutChange());
      } else {
        // Traditional mode checks integPackage too
        return !!(connName || systemType || integPackage || systemId || !areSystemPropsWithoutChange());
      }
    }
  }, [
    areSystemPropsWithoutChange,
    connName,
    integPackage,
    props.system,
    props.isStandaloneMode,
    systemId,
    systemType,
  ]);

  const isFormDataChanged = wasFormChanged();



useMemo(() => {
  if (systemType !== undefined) {
    setIsPropertiesLoading(true);
    // Only reset if not editing
    if (props.system === undefined) {
      setIntegPackage(undefined);
      setSystemId(undefined);
    }
    // Add query parameter for traditional mode
    const baseEndpoint = `/api/Structure/Connectors/${systemType.name}/properties`;
    const endpoint = props.isStandaloneMode 
      ? baseEndpoint 
      : `${baseEndpoint}?includePackages=true`;

    const url = getUrlBasedOnEnv(endpoint);
    const propsRunId = crypto.randomUUID();
    void fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: propsRunId,
      },
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, response.headers.get("X-Run-Id") ?? propsRunId);
          return;
        }
        return response.json();
      })
      .then(function (data) {
        if (!data) {
          return;
        }
        const systemProps: SystemProperty[] =
          mapDataToModels<SystemProperty>(data.properties);
        setSystemProperties(systemProps);
        const nodEditedProps = getSystemPropsDynObject(systemProps);
        setNotEditedSysPropsDynObj(nodEditedProps);
        // Load integration packages if traditional mode
        if (!props.isStandaloneMode && data.availableIntegPackages) {
          const intPacks: IntegPackage[] = mapDataToModels<IntegPackage>(
            data.availableIntegPackages,
          );
          setIntegPackages(intPacks);
        }

        // Populate systemId when editing an existing credential
        if (props.system !== undefined) {
          setSystemId(props.system.systemId);
          setIsEditingExisting(true);
        }
      })
      .finally(() => setIsPropertiesLoading(false));
  }
}, [accessToken, dispatch, systemType, props.isStandaloneMode, props.system]);

  const systemOptions = useMemo(
    () =>
      systemTypes.map(
        (system) =>
          ({
            label: system.name,
            value: system,
            icon: (
              <img
                alt="icon"
                src={`data:image/jpeg;base64,${system.icon}`}
                style={{ width: 20, height: 20 }}
              />
            ),
          }),
      ),
    [systemTypes],
  );

  const integPackageOptions = useMemo(
    () =>
      integPackages.map(
        (pack) =>
          ({
            label: pack.integPackageName,
            value: pack,
          }),
      ),
    [integPackages],
  );


  useEffect(() => {
    if (!props.isStandaloneMode && integPackage !== undefined) {
      if (props.system !== undefined) {
        // Editing- clear systemId if it's not in the new package
        if (integPackage.systemIds?.findIndex(
          (id) => id === (props.system as SystemInList).systemId,
        ) === -1
        ) {
          setSystemId(undefined);
        }
      } else {
        // Creating new- clear systemId when package changes
        setSystemId(undefined);
      }
    }
  }, [integPackage, props.system, props.isStandaloneMode]);

  const systemIdsOptions = useMemo(() => {
    if (integPackage) {
      return integPackage?.systemIds?.map(
        (sysId) =>
          ({
            label: sysId,
            value: sysId,
          }),
      );
    } else {
      return [];
    }
  }, [integPackage]);

  const setEditedSystemProps = useCallback(
    (systems: SystemType[]) => {
      if (props.system !== undefined) {
        setConnName(props.system.systemName);
        const sysType = systems.find(
          (t) => t.name === (props.system as SystemInList).systemType,
        );
        setSystemType(sysType);
      }
    },
    [props.system],
  );

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    setLoading(true);

    const url = getUrlBasedOnEnv("/api/Structure/Connectors?withIcons=true");
    const connectorsRunId = crypto.randomUUID();
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: connectorsRunId,
      },
      signal: abortControllerRef.current.signal,
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, response.headers.get("X-Run-Id") ?? connectorsRunId);
          return;
        }
        return response.json();
      })
      .then(function (data) {
        if (!data) {
          return;
        }
        const systems: SystemType[] = mapDataToModels<SystemType>(
          data,
        );
        setSystemTypes(systems);
        setEditedSystemProps(systems);
      })
      .catch((error: any) => {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, connectorsRunId, true);
        }
      })
      .finally(() => setLoading(false));

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    urlParameters,
    props.params,
    accessToken,
    setEditedSystemProps,
    dispatch,
  ]);

  function getSystemPropsDynObject(
    sysProperties: SystemProperty[],
  ): DynamicObject {
    const dynObj: DynamicObject = {};
    sysProperties.forEach((sysProp) => {
      dynObj[sysProp.name] =
        sysProp.defaultValue !== undefined && sysProp.defaultValue !== null
          ? sysProp.defaultValue.toString()
          : "";
    });
    return dynObj;
  }

  function getSaveJson() {
    // Fields hidden by the token rules are not sent, so stale values never reach the connector.
    const credentials: DynamicObject = getSystemPropsDynObject(
      getVisibleProperties(systemProperties),
    );

    const saveCredentialDto: SaveUserSystemCredentialDto = {
      name: connName as string,
      systemType: (systemType as SystemType).name,
      systemId: systemId as string,
      credentials,
    };
    const jsonBody = JSON.stringify(saveCredentialDto);

    return jsonBody;
  }

  const saveCredential = async () => {
    setLoading(true);

    let path = `/edfs/itwins/${props.iTwinId}/systems`;
    // Remove /edfs from the url if the edfsUrl is different from iTwinPlatformUrl (R&D)
    path = ConfigManager.removeEdfsIfNeeded(path);

    let saveUrl = ConfigManager.edfsUrl + path;
    if (props.system !== undefined) {
      saveUrl += `/${props.system.id}`;
    }
    const jsonBody = getSaveJson();
    const saveRunId = crypto.randomUUID();
    const fetchResponse = await fetch(saveUrl, {
      method: props.system !== undefined ? "PUT" : "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: saveRunId,
      },
      body: jsonBody,
    }).then(async function (response) {
      checkInternalServerError(response.status, dispatch, saveRunId);
      return response.json();
    });
    setLoading(false);
    if (fetchResponse.error) {
      showToast({
        status: "negative",
        message: `${fetchResponse.error.message} ActivityId="${saveRunId}"`,
      });
    } else {
      showToast({ status: "positive", action: props.system !== undefined ? "update" : "create" });
      closeFRoll(true);
    }
  };

  const onCancelClick = () => {
    if (isFormDataChanged) {
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: () => closeFRoll(false),
          caption: "Discard unsaved changes?",
          text: "Changes you made so far will not be updated",
          okText: "Discard anyway",
          cancelText: "Stay",
        }),
      );
    } else {
      closeFRoll(false);
    }
  };

  const closeFRoll = (refreshList: boolean) => {
    if (props.closeFrollFunction !== undefined) {
      props.closeFrollFunction(refreshList);
    }
  };

  return (
    <div
      style={{ height: "100%", overflowY: "hidden" }}
      className="iuiBackgroundColor"
    >
      {isLoading ? (
        <div
          className="becs-ltr-margin"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <ProgressRadial indeterminate />
        </div>
      ) : (
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {props.isFromUrl && (
            <div className="becs-all-margin">
              <div className="rowWrap">
                <Text as="h3" variant="title">
                  New Credential management
                </Text>
              </div>
            </div>
          )}
          <div
            className="becs-infopanelbody-footer-line"
            style={{
              flex: 1,
              overflowY: "auto",
            }}
          >
            <div className="becs-all-margin">
              <LabeledInputComponent
                required={true}
                validationType={EfFieldValidationTypeEnum.Name200}
                displayStyle="default"
                label="User connection name"
                placeholder="Enter text here..."
                maxLength={200}
                key="LbInConnName"
                defaultValue={connName}
                onChange={(currentValue) => {
                  setConnName(currentValue.target.value);
                }}
                onStatusChange={(status) => {
                  setConnNameStatus(status);
                }}
              />

              <div className="becs-t-margin">
                <LabeledSelect
                  required={true}
                  label={"System type"}
                  options={systemOptions}
                  onChange={(currentValue: SystemType) => {
                    setSystemType(currentValue);
                  }}
                  value={systemType}
                />
              </div>

              {isPropertiesLoading ? (
                <div
                  className="becs-ltr-margin"
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <ProgressRadial indeterminate />
                </div>
              ) : (
                <div>
                  {/* Only show Integration Package dropdown in traditional mode */}
                  {systemType && !props.isStandaloneMode && (
                    <div className="becs-t-margin">
                      <LabeledSelect
                        required={props.system === undefined}
                        label={"Integration package"}
                        options={integPackageOptions}
                        onChange={(currentValue: IntegPackage) => {
                          setIntegPackage(currentValue);
                        }}
                        value={integPackage}
                      />
                    </div>
                  )}
                  {systemType && (
                    <div>
                      <div className="becs-t-margin">
                        {/* Standalone mode: text input with regex validation */}
                        {props.isStandaloneMode ? (
                          <LabeledInputComponent
                            label="System ID"
                            required={true}
                            validationType={EfFieldValidationTypeEnum.SystemId}
                            defaultValue={systemId}
                            onChange={(e) => setSystemId(e.target.value)}
                            onStatusChange={(status) => {
                              setSystemIdStatus(status);
                            }}
                          />
                        ) : (
                          /* Traditional mode */
                          <>
                            {/* Show as read-only if editing & Integreation Package not selected*/}
                            {isEditingExisting && integPackage === undefined ? (
                              <LabeledInputComponent
                                label="System ID"
                                required={true}
                                validationType={EfFieldValidationTypeEnum.SystemId}
                                defaultValue={systemId}
                                disabled={true}
                                onChange={() => {}}
                              />
                            ) : (
                              <LabeledSelect
                                required={true}
                                label={"System ID"}
                                options={systemIdsOptions}
                                onChange={(currentValue: string) => {
                                  setSystemId(currentValue);
                                }}
                                value={systemId}
                              />
                            )}
                          </>
                        )}
                      </div>
                      {isEditingExisting && (
                        <div className="becs-tdouble-margin">
                          <Alert>
                            For your privacy and safety, editing options are
                            limited. Please fill in the following fields again
                            to proceed.
                          </Alert>
                        </div>
                      )}
                    </div>
                  )}
                  <SystemProperties
                    systemProperties={systemProperties}
                    onSystemPropertiesChange={setSystemProperties}
                  />
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div className="becs-all-margin">
              <div className="rowWrap">
                <Button
                  key="saveCredentialBtn"
                  as="button"
                  onClick={() => { void saveCredential(); }}
                  styleType="cta"
                  disabled={!(isFormValid && isFormDataChanged)}
                >
                  <div>{props.system === undefined ? "Save" : "Update"}</div>
                </Button>
                <Button
                  key="cancelCredentialBtn"
                  as="button"
                  onClick={() => onCancelClick()}
                >
                  <div>Cancel</div>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CredentialsForm;
